import { menuItems, type MenuItem } from '../menuItems'
import type { PageGuide } from '../../components/guide/guideTypes'
import { buildDetailedGuide, type ScreenMeta } from './guideFactory'

function flattenMenu(items: MenuItem[], acc: ScreenMeta[] = []): ScreenMeta[] {
  for (const item of items) {
    if (item.path) {
      acc.push({ path: item.path, label: item.label })
    }
    if (item.children?.length) flattenMenu(item.children, acc)
  }
  return acc
}

/** Extra operational screens that are not always listed as menu leaves */
const extraScreens: ScreenMeta[] = [
  { path: '/dashboard', label: { ar: 'لوحة التحكم', en: 'Dashboard', fr: 'Tableau de bord' } },
  { path: '/invoices/new', label: { ar: 'فاتورة مبيعات جديدة', en: 'New sales invoice', fr: 'Nouvelle facture' } },
  { path: '/purchase-invoices/new', label: { ar: 'فاتورة شراء جديدة', en: 'New purchase invoice', fr: 'Nouvel achat' } },
  { path: '/sales-orders/new', label: { ar: 'أمر بيع جديد', en: 'New sales order', fr: 'Nouvelle commande' } },
  { path: '/purchase-orders/new', label: { ar: 'أمر شراء جديد', en: 'New purchase order', fr: 'Nouvelle commande achat' } },
  { path: '/quotations/new', label: { ar: 'عرض سعر جديد', en: 'New quotation', fr: 'Nouveau devis' } },
  { path: '/sales-returns/new', label: { ar: 'مرتجع مبيعات جديد', en: 'New sales return', fr: 'Nouveau retour' } },
  { path: '/purchase-returns/new', label: { ar: 'مرتجع مشتريات جديد', en: 'New purchase return', fr: 'Nouveau retour achat' } },
  { path: '/journal/new', label: { ar: 'قيد يومية جديد', en: 'New journal entry', fr: 'Nouvelle écriture' } },
  { path: '/vouchers/new', label: { ar: 'سند جديد', en: 'New voucher', fr: 'Nouvelle pièce' } },
  { path: '/items/new', label: { ar: 'صنف جديد', en: 'New item', fr: 'Nouvel article' } },
  { path: '/customers/new', label: { ar: 'عميل جديد', en: 'New customer', fr: 'Nouveau client' } },
  { path: '/vendors/new', label: { ar: 'مورد جديد', en: 'New vendor', fr: 'Nouveau fournisseur' } },
  { path: '/branch-transfers/new', label: { ar: 'مناقلة فروع جديدة', en: 'New branch transfer', fr: 'Nouveau transfert' } },
  { path: '/inventory-adjustments/new', label: { ar: 'تسوية جردية جديدة', en: 'New inventory adjustment', fr: 'Nouvel ajustement' } },
  { path: '/opening-stock-invoices/new', label: { ar: 'مخزون أول المدة جديد', en: 'New opening stock', fr: 'Nouveau stock d’ouverture' } },
  { path: '/production/boms/new', label: { ar: 'وصفة إنتاج جديدة', en: 'New BOM', fr: 'Nouvelle nomenclature' } },
  { path: '/production/orders/new', label: { ar: 'أمر إنتاج جديد', en: 'New production order', fr: 'Nouvel OF' } },
  { path: '/sorting/new', label: { ar: 'أمر فرز جديد', en: 'New sorting order', fr: 'Nouvel ordre de tri' } },
  { path: '/fixed-assets/new', label: { ar: 'أصل ثابت جديد', en: 'New fixed asset', fr: 'Nouvelle immobilisation' } },
  { path: '/budgets/new', label: { ar: 'موازنة جديدة', en: 'New budget', fr: 'Nouveau budget' } },
  { path: '/companies/new', label: { ar: 'شركة جديدة', en: 'New company', fr: 'Nouvelle société' } },
  { path: '/subscriptions/invoices/new', label: { ar: 'فاتورة اشتراك جديدة', en: 'New subscription invoice', fr: 'Nouvelle facture abonnement' } },
  { path: '/invoices/dental/new', label: { ar: 'فاتورة أسنان جديدة', en: 'New dental invoice', fr: 'Nouvelle facture dentaire' } },
  { path: '/contact-accounts/transfer', label: { ar: 'تحويل حساب جهة اتصال', en: 'Contact account transfer', fr: 'Transfert de compte' } },
  { path: '/customer-categories', label: { ar: 'فئات العملاء', en: 'Customer categories', fr: 'Catégories clients' } },
  { path: '/inventory/quick-stock-take', label: { ar: 'جرد سريع', en: 'Quick stock take', fr: 'Inventaire rapide' } },
  { path: '/inventory-transfers/new', label: { ar: 'مناقلة مخزون', en: 'Inventory transfer', fr: 'Transfert stock' } },
  { path: '/purchase-orders/:id/receive', label: { ar: 'استلام بضاعة أمر شراء', en: 'Goods receipt', fr: 'Réception marchandises' } },
]

function uniqueByPath(list: ScreenMeta[]): ScreenMeta[] {
  const map = new Map<string, ScreenMeta>()
  for (const s of list) {
    if (!map.has(s.path)) map.set(s.path, s)
  }
  return [...map.values()]
}

/**
 * Detailed guide for every menu screen + common /new routes.
 * Longer paths are preferred by the resolver scoring.
 */
export const generatedPageGuides: PageGuide[] = uniqueByPath([
  ...flattenMenu(menuItems),
  ...extraScreens,
]).map((meta) => buildDetailedGuide(meta))
